import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { XMarkIcon, CreditCardIcon } from '@heroicons/react/24/outline';
import axiosInstance from '../api/axiosInstance';
import logger from '../../utils/logger';

// Initialize Stripe - use your public key from env
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#32325d',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4'
      }
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a'
    }
  },
  hidePostalCode: true  // Hide built-in postal code, we'll use custom input
};

const PaymentMethodForm = ({ onSuccess, onCancel, showNotification, currentPlan }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [postalCode, setPostalCode] = useState('');
  const [billingDetails, setBillingDetails] = useState({
    name: '',
    email: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Step 1: Create setup intent from backend (uses BusinessMail customer)
      const { data: setupData } = await axiosInstance.post('/api/invoices/setup-intent');

      if (!setupData.success) {
        throw new Error(setupData.message || 'Failed to create setup intent');
      }

      const { clientSecret } = setupData.data;

      // Step 2: Confirm card setup with Stripe
      const { setupIntent, error: stripeError } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: billingDetails.name || undefined,
            email: billingDetails.email || undefined,
            address: {
              postal_code: postalCode || undefined
            }
          }
        }
      });

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      // Step 3: Save payment method to BusinessMail record
      // The setup intent has already attached the payment method to the Stripe customer
      // Now we need to save it to the BusinessMail document and set as default
      const { data: saveData } = await axiosInstance.post('/api/invoices/save-payment-method', {
        setupIntentId: setupIntent.id
      });

      if (saveData.success) {
        showNotification?.('Payment method added successfully!', 'success');
        onSuccess(saveData.data.paymentMethod);
      } else {
        throw new Error(saveData.message || 'Failed to save payment method');
      }

    } catch (err) {
      logger.error('Error adding payment method:', err);
      setError(err.message || 'An error occurred. Please try again.');
      showNotification?.(err.message || 'Failed to add payment method', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Cardholder Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Cardholder Name
        </label>
        <input
          type="text"
          value={billingDetails.name}
          onChange={(e) => setBillingDetails({ ...billingDetails, name: e.target.value })}
          placeholder="John Doe"
          className="w-full p-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
        />
      </div>

      {/* Email (optional) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Email <span className="text-gray-400 text-xs">(optional)</span>
        </label>
        <input
          type="email"
          value={billingDetails.email}
          onChange={(e) => setBillingDetails({ ...billingDetails, email: e.target.value })}
          placeholder="john@example.com"
          className="w-full p-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
        />
      </div>

      {/* Card Information */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Card Information
        </label>
        <div className="p-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a]">
          <CardElement options={CARD_ELEMENT_OPTIONS} />
        </div>
      </div>

      {/* Postal/ZIP Code */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Postal/ZIP Code
        </label>
        <input
          type="text"
          value={postalCode}
          onChange={(e) => setPostalCode(e.target.value)}
          placeholder="12345 or AB1 2CD"
          maxLength={10}
          className="w-full p-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
        />
      </div>

      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] rounded-lg transition-colors"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || loading}
          className="px-6 py-2 bg-orange-600 hover:bg-orange-700 dark:bg-orange-700 dark:hover:bg-orange-800 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
              Processing...
            </>
          ) : (
            <>
              <CreditCardIcon className="w-4 h-4" />
              Save Card
            </>
          )}
        </button>
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
        Your payment information is encrypted and secure. We never store your card details.
      </p>
    </form>
  );
};

const PaymentMethodModal = ({ isOpen, onClose, onSuccess, showNotification, currentPlan }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-[#1a1a1a] rounded-lg max-w-md w-full shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
              <CreditCardIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Add Payment Method</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">Securely save your card for billing</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <Elements stripe={stripePromise}>
            <PaymentMethodForm
              onSuccess={(paymentMethod) => {
                onSuccess(paymentMethod);
                onClose();
              }}
              onCancel={onClose}
              showNotification={showNotification}
              currentPlan={currentPlan}
            />
          </Elements>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodModal;
